import { ROTAS } from '@remoto/shared';
import { chamar } from '../api';

/**
 * A chave VAPID chega em base64url e o navegador quer bytes crus.
 *
 * Base64url troca `+/` por `-_` e dispensa o preenchimento; `atob` não sabe
 * nada disso, então a conversão volta ao base64 comum antes de decodificar.
 */
function chaveEmBytes(base64url: string): ArrayBuffer {
  const preenchido = base64url.padEnd(base64url.length + ((4 - (base64url.length % 4)) % 4), '=');
  const bruto = atob(preenchido.replace(/-/g, '+').replace(/_/g, '/'));
  const bytes = new Uint8Array(bruto.length);
  for (let i = 0; i < bruto.length; i += 1) bytes[i] = bruto.charCodeAt(i);
  return bytes.buffer;
}

function paraBase64(buffer: ArrayBuffer | null): string {
  if (!buffer) return '';
  return btoa(String.fromCharCode(...new Uint8Array(buffer)));
}

export type EstadoDoAviso = 'indisponivel' | 'desligado' | 'negado' | 'ligado';

export function avisosDisponiveis(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window
  );
}

/**
 * Registra o service worker.
 *
 * Registrar não pede permissão nenhuma e não guarda nada: este worker não tem
 * cache (o painel é privado). O que ele faz é existir — sem ele registrado, o
 * navegador não tem onde entregar um push, por mais que a permissão esteja
 * concedida.
 */
export async function registrarSw(): Promise<ServiceWorkerRegistration | null> {
  if (!avisosDisponiveis()) return null;
  const registro = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  await navigator.serviceWorker.ready;
  return registro;
}

/** O que o navegador permite hoje, sem pedir nada a ninguém. */
export async function estadoDoAviso(): Promise<EstadoDoAviso> {
  if (!avisosDisponiveis()) return 'indisponivel';
  if (Notification.permission === 'denied') return 'negado';
  const registro = await navigator.serviceWorker.getRegistration();
  const inscricao = await registro?.pushManager.getSubscription();
  return inscricao ? 'ligado' : 'desligado';
}

async function gravarInscricao(inscricao: PushSubscription): Promise<void> {
  await chamar(ROTAS.notificacoes.inscricoes, {
    method: 'POST',
    corpo: {
      endpoint: inscricao.endpoint,
      chaves: {
        p256dh: paraBase64(inscricao.getKey('p256dh')),
        auth: paraBase64(inscricao.getKey('auth')),
      },
      apelido: navigator.userAgent.slice(0, 60),
    },
  });
}

/**
 * Põe este aparelho em dia, toda vez que o painel abre.
 *
 * É o conserto do aparelho que "tinha avisos ligados" e não recebia nada. Três
 * coisas quebravam em silêncio, e as três morrem aqui:
 *
 * 1. **O service worker só era registrado dentro do `ligarAvisos`**, e o botão
 *    de ligar morava numa página de administrador. Quem nunca entrou lá nunca
 *    teve worker — e sem worker não há entrega, mesmo com a permissão dada.
 * 2. **O endereço de push troca sozinho.** Reinstalar o aplicativo, limpar
 *    dados ou uma decisão do próprio navegador geram um endereço novo; o antigo
 *    passa a responder 404/410 e o servidor o apaga (fazendo certo). Sem
 *    reinscrever, o aparelho fica calado para sempre.
 * 3. **`pushsubscriptionchange` não resolve**: ele roda dentro do worker, que
 *    não tem o token de acesso da pessoa e não consegue avisar a API.
 *
 * Nada aqui pede permissão: só age quando ela já foi concedida. E nada aqui
 * lança — abrir o painel não pode falhar porque a inscrição não deu certo.
 */
export async function sincronizarAvisos(): Promise<void> {
  try {
    if (!avisosDisponiveis()) return;
    const registro = await registrarSw();
    if (!registro || Notification.permission !== 'granted') return;

    const jaTem = await registro.pushManager.getSubscription();
    if (jaTem) {
      // O POST é idempotente (o servidor faz upsert pelo endereço) e serve de
      // sinal de vida: é ele que mantém o `usadoEm` da lista de aparelhos.
      await gravarInscricao(jaTem);
      return;
    }

    const { chave } = await chamar<{ chave: string }>(ROTAS.notificacoes.chavePublica);
    if (!chave) return;
    const nova = await registro.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: chaveEmBytes(chave),
    });
    await gravarInscricao(nova);
  } catch {
    // Sem VAPID configurada, sem rede, navegador sem suporte: nada disso pode
    // atrapalhar quem só quer usar o painel.
  }
}

/**
 * Liga os avisos neste aparelho.
 *
 * Pede a permissão só quando alguém clica: navegador que recebe o pedido sem
 * o gesto de uma pessoa costuma negar de vez, e negar de vez não tem volta
 * pela página — a pessoa teria de mexer nas configurações do navegador.
 */
export async function ligarAvisos(): Promise<EstadoDoAviso> {
  if (!avisosDisponiveis()) return 'indisponivel';

  const permissao = await Notification.requestPermission();
  if (permissao !== 'granted') return permissao === 'denied' ? 'negado' : 'desligado';

  const registro = await registrarSw();
  if (!registro) return 'indisponivel';

  const { chave } = await chamar<{ chave: string }>(ROTAS.notificacoes.chavePublica);
  const inscricao =
    (await registro.pushManager.getSubscription()) ??
    (await registro.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: chaveEmBytes(chave),
    }));

  await gravarInscricao(inscricao);

  return 'ligado';
}

/**
 * Desliga neste aparelho, dos dois lados.
 *
 * O servidor apaga por endereço porque é o endereço que o navegador conhece —
 * o id do banco ele nunca viu. Cancelar só no navegador deixaria o servidor
 * mandando avisos para um endereço morto até algum envio falhar.
 */
export async function desligarAvisos(): Promise<EstadoDoAviso> {
  const registro = await navigator.serviceWorker.getRegistration();
  const inscricao = await registro?.pushManager.getSubscription();
  if (inscricao) {
    const endpoint = inscricao.endpoint;
    await inscricao.unsubscribe();
    await chamar(ROTAS.notificacoes.inscricoes + '/remover', {
      method: 'POST',
      corpo: { endpoint },
    });
  }
  return 'desligado';
}
